import React, { useCallback, useContext, useState } from "react";
import { ModuleWrapper } from "components/fragments";
import { useMount, useRouter, useApi, useFilter } from "hooks";
import { getGuestCheckinInfo, searchTransactionNew, getGuestCheckin, guestUnSyncTransactions } from "apis";
import { guestCheckinInfo, guestCheckin, transactionListFilterRequest } from "mappers";
import {
  GuestDetailsSkeleton,
  GuestDetailsHeader,
  GuestCheckinInfo,
  GuestRecentTransaction,
  GuestTagInfo,
} from "./";
import { VenueContext } from "contexts";
import { mapObject } from "services";
import { transactionListFilterState } from "modules/transaction/transaction-list/filters";
import { guestRecentTransactionResponse, guestUnSyncTransaction } from "mappers/guest.mapper";
import { mixpanel, TrackEvent } from "mixpanel";
import lang from "translations";
import { TabBar, Pill, DataTable } from "components";
import { PillType } from "enums";
import { unSyncTransactionReportRequest } from "mappers/unsync-transaction-report.mapper";

const GuestDetails = () => {
  const { venue } = useContext(VenueContext);
  const { query } = useRouter();
  const { id } = query;
  const { venueId } = venue;
  const [selectedGuestCheckinId, setSelectedGuestCheckinId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const { requestState } = useFilter(
    transactionListFilterState(venue, {
      query,
      guestCheckinId: selectedGuestCheckinId,
    })
  );

  useMount(async () => {
    mixpanel.track(TrackEvent.VisitedPage, {
      Page: lang.guestDetails,
    });
    try {
      const res = await fetchCheckinInfo();
      const { currentCheckin = {}, guestCheckinIds } = res;
      let guestCheckInId = currentCheckin?.checkinId;
      if (!guestCheckInId && guestCheckinIds.length) {
        guestCheckInId = guestCheckinIds[0].checkinId;
      }
      setSelectedGuestCheckinId(guestCheckInId);
      fetchGuestApis(guestCheckInId);
    } catch (e) {
      console.log(e);
    }
  });

  const fetchGuestApis = (id) => {
    requestGuestCheckIn({
      checkinId: id,
    });
    fetchTransactions(requestState, id);
  };

  const {
    request: fetchCheckinInfo,
    loading: loadingGuestCheckinInfo,
    mappedData: mappedGuestCheckinInfo,
    result: totalUnSyncTransaction = {  metadata: { totalUnSyncTransaction: 0 } },
    error: errorCheckInInfo,
  } = useApi({
    api: getGuestCheckinInfo,
    params: { guestId: id, venueId },
    mapper: guestCheckinInfo,
    handleOwnError: true,
    returnMappedData: true,
  });

  const {
    request: requestGuestCheckIn,
    mappedData: mappedGuestCheckin,
    loading: guestCheckinLoading,
    error: guestCheckInError,
  } = useApi({
    api: getGuestCheckin,
    params: { guestId: id, venueId },
    mapper: guestCheckin,
    handleOwnError: true,
  });

  const transactionRequest = useApi({
    api: searchTransactionNew,
    params: { guestId: id, venueId, page: 1, pageSize: 5 },
    isArray: true,
    mapper: guestRecentTransactionResponse,
    handleOwnError: true,
  });

  const { loading: transactionLoading = true } = transactionRequest;

  const { modifyFilters: modifyUnSyncFilters, filterState: unSyncFilterState, requestState: unSyncRequestState } = useFilter(
    {
      page: 1,
      pageSize: 20
    }
  );

  const {
    request: fetchUnSyncTransactions,
    loading: loadingUnSyncTransactions,
    mappedData: mappedUnSyncTransactions = [],
    result: unSyncTransactionsResult = { data: [], metadata: { total: 0 } },
    error: unSyncError,
  } = useApi({
    api: guestUnSyncTransactions,
    isArray: true,
    handleOwnError: true,
    mapper: guestUnSyncTransaction,
    returnMappedData: true,
  });

  const fetchTransactions = useCallback(
    (requestState, guestCheckinId) => {
      const params = mapObject(requestState, transactionListFilterRequest);
      params.startDateTime = null;
      params.endDateTime = null;
      params.guestCheckinId = guestCheckinId;
      params.searchKey = null;
      params.pageSize = 20;
      params.transactionStatus = null;
      transactionRequest.request(params);
    },
    [transactionRequest]
  );

  const fetchUnSyncTransactionsList = useCallback(
    (requestState) => {
      const params = mapObject(requestState, unSyncTransactionReportRequest);
      params.venueId = venueId;
      params.guestCheckinId = selectedGuestCheckinId;
      params.guestId = id;
      fetchUnSyncTransactions(params);
    },
    [fetchUnSyncTransactions, venueId, selectedGuestCheckinId, id]
  );
  // console.log(mappedGuestCheckin);
  
  const tabItems = [
    { id: 0, text: lang.recentTransactions || "Recent Transactions" },
  ];

  // Add second tab only if totalUnSyncTransaction is greater than 0
  if (totalUnSyncTransaction?.metadata?.totalUnSyncTransaction > 0) {
    tabItems.push({ 
      id: 1, 
      text: (
        <div className="flex items-center gap-sm">
          <span>{lang.unsyncTransactions || "Unsync Transactions"}</span>
          <Pill type={PillType.Red} size="text-xs">
            {totalUnSyncTransaction.metadata.totalUnSyncTransaction}
          </Pill>
        </div>
      )
    });
  }

  return loadingGuestCheckinInfo ? (
    <GuestDetailsSkeleton />
  ) : (
    <ModuleWrapper
      error={errorCheckInInfo}
      header={
        <GuestDetailsHeader
          mappedData={mappedGuestCheckinInfo}
          mappedGuestCheckin={mappedGuestCheckin}
          loading={loadingGuestCheckinInfo}
        />
      }
    >
      <div>
        <GuestCheckinInfo
          guestId={id}
          venueId={venueId}
          loading={loadingGuestCheckinInfo || guestCheckinLoading}
          mappedData={mappedGuestCheckinInfo}
          guestCheckinIds={mappedGuestCheckinInfo.guestCheckinIds}
          setSelectedGuestCheckinId={async (id) => {
            fetchGuestApis(id);
            setSelectedGuestCheckinId(id);
          }}
          selectedGuestCheckinId={selectedGuestCheckinId}
          mappedGuestCheckin={mappedGuestCheckin}
          lastSyncedDate={transactionRequest.mappedData[0]?.deviceCreatedDate}
          onForceCheckout={() => {
            fetchGuestApis(selectedGuestCheckinId);
          }}
          error={guestCheckInError}
        />
      </div>

      {/* Tab Navigation */}
      <TabBar 
        className="mb-md" 
        items={tabItems}
        activeTab={activeTab}
        onTabChange={(tabId) => {
          setActiveTab(tabId);
          if (tabId === 1 && totalUnSyncTransaction?.metadata?.totalUnSyncTransaction > 0) {
            fetchUnSyncTransactionsList(unSyncRequestState);
          }
        }}
      />

      {/* Tab Content */}
      {activeTab === 0 && (
        <GuestRecentTransaction
          guestCheckinId={selectedGuestCheckinId}
          venueId={venueId}
          loading={transactionLoading}
          mappedData={transactionRequest.mappedData}
        />
      )}
      {activeTab === 1 && totalUnSyncTransaction?.metadata?.totalUnSyncTransaction > 0 && (
        <DataTable
          page={unSyncFilterState.page}
          pageSize={unSyncFilterState.pageSize}
          onChangePage={modifyUnSyncFilters}
          fetchList={(requestState) => fetchUnSyncTransactionsList(requestState)}
          total={unSyncTransactionsResult.metadata.total}
          loading={loadingUnSyncTransactions}
          columns={[
            { key: "transactionId", text: lang.transactionId || "Transaction ID" },
            { key: "platform", text: lang.platform || "Platform" },
            { key: "transactionType", text: lang.transactionType || "Type" },
            { key: "productName", text: lang.productName || "Product" },
            { key: "message", text: lang.message || "Message" },
            { key: "retryCount", text: lang.retryCount || "Retry Count" },
            { key: "syncTime", text: lang.syncTime || "Sync Time" },
          ]}
          data={mappedUnSyncTransactions}
          error={unSyncError}
          pageable={true}
        />
      )}

      <GuestTagInfo
        guestId={id}
        venueId={venueId}
        loading={loadingGuestCheckinInfo || guestCheckinLoading}
        mappedGuestCheckin={mappedGuestCheckin}
        selectedGuestCheckinId={selectedGuestCheckinId}
        setSelectedGuestCheckinId={(checkInId) => {
          setSelectedGuestCheckinId(checkInId);
          fetchTransactions(requestState, checkInId);
        }}
        error={guestCheckInError || errorCheckInInfo}
      ></GuestTagInfo>
    </ModuleWrapper>
  );
};

export default GuestDetails;
